import {
  BOL_POST_SIGN,
  BOL_POST_CANCEL,
  BOL_POST_RELEASE,
  BOL_POST_CONFIRM_CANCEL,
  BOL_POST_SEND_TO_COLLECT,
  MANIFEST_POST_RELEASE,
  MANIFEST_POST_SIGN_CUSTOM,
  INDOSSAMENT_POST_ADD,
  MANIFEST_CORRECTOR_POST_SIGN
} from "../axios";
import { useWeb3Handler } from "../axiosBlockchainReq";
import { useInvalidateBol, useInvalidateMan } from "./cashInvalidators";

/**
 * Constructor hook for other sign hooks
 * @param {{
 *  axios : import("axios").AxiosInstance,
 *  makeRequest : (params : {id : string, body : *}) => Promise<import("axios").AxiosResponse>,
 *  cashInvalidator : (id : string) => void
 * }}
 * @returns {SignHookReturn}
 */

function useSign({ axios, makeRequest, cashInvalidator }) {
  const web3Handler = useWeb3Handler(axios);

  return ({ id, body }, callbacks, description) => {
    return makeRequest({ id, body })
      .then(web3Handler(description))
      .then(cashInvalidator(id))
      .then(callbacks?.onSuccess ?? (() => {}))
      .catch(callbacks?.onError ?? (() => {}))
      .then(callbacks?.onFinally ?? (() => {}));
  };
}

function useSignManCorrector({ axios, makeRequest, cashInvalidator }) {
  const web3Handler = useWeb3Handler(axios);

  return ({ id, manCorId, body }, callbacks, description) => {
    return makeRequest({ id, manCorId, body })
        .then(web3Handler(description))
        .then(cashInvalidator(id))
        .then(callbacks?.onSuccess ?? (() => {}))
        .catch(callbacks?.onError ?? (() => {}))
        .then(callbacks?.onFinally ?? (() => {}));
  };
}

/**
 * Creates trigger function postSignBol
 * @type {SignHook}
 */
export function usePostSignBol(axios, cargoCaseDocId) {
  return useSign({
    makeRequest: ({ id, body }) => axios.post(BOL_POST_SIGN + id, body),
    cashInvalidator: useInvalidateBol(cargoCaseDocId),
    axios,
  });
}

/**
 * Creates trigger function postReleaseBol
 * @type {SignHook}
 */
export function usePostReleaseBol(axios, cargoCaseDocId) {
  return useSign({
    makeRequest: ({ id, body }) => axios.post(BOL_POST_RELEASE + id, body),
    cashInvalidator: useInvalidateBol(cargoCaseDocId),
    axios,
  });
}

/**
 * Creates trigger function postAnnulateBol
 * @type {SignHook}
 */
export function usePostAnnulateBol(axios, cargoCaseDocId) {
  return useSign({
    makeRequest: ({ id, body }) => axios.post(BOL_POST_CANCEL + id, body),
    cashInvalidator: useInvalidateBol(cargoCaseDocId),
    axios,
  });
}

/**
 * Creates trigger function postConfirmAnnulateBol
 * @type {SignHook}
 */
export function usePostConfirmAnnulateBol(axios, cargoCaseDocId) {
  const trigger = useSign({
    makeRequest: ({ id, body }) =>
      axios.post(BOL_POST_CONFIRM_CANCEL + id, body),
    cashInvalidator: useInvalidateBol(cargoCaseDocId),
    axios,
  });

  return (makeRequestOptions, callbacks, description) =>
    trigger(makeRequestOptions, callbacks, description ?? "Confirm annulate");
}

/**
 * Creates trigger function postSendToCollectBol
 * @type {SignHook}
 */
export function usePostSendToCollectBol(axios, cargoCaseDocId) {
  return useSign({
    makeRequest: ({ id, body }) =>
      axios.post(BOL_POST_SEND_TO_COLLECT + id, body),
    cashInvalidator: useInvalidateBol(cargoCaseDocId),
    axios,
  });
}

/**
 * Creates trigger function postReleaseMan
 * @type {SignHook}
 */
export function usePostReleaseMan(axios, cargoCaseDocId) {
  return useSign({
    makeRequest: ({ id, body }) => axios.post(MANIFEST_POST_RELEASE + id, body),
    cashInvalidator: useInvalidateMan(cargoCaseDocId),
    axios,
  });
}

/**
 * Creates trigger function postSignCustomMan
 * @type {SignHook}
 */
export function usePostSignCustomMan(axios, cargoCaseDocId) {
  return useSign({
    makeRequest: ({ id, body }) =>
      axios.post(MANIFEST_POST_SIGN_CUSTOM + id, body),
    cashInvalidator: useInvalidateMan(cargoCaseDocId),
    axios,
  });
}

/**
 * Creates trigger function postSignCustomMan
 * @type {SignHook}
 */
export function usePostSignManCorrector(axios, cargoCaseDocId) {
  return useSignManCorrector({
    makeRequest: ({ id, manCorId, body }) =>
        axios.post(MANIFEST_CORRECTOR_POST_SIGN + manCorId, body),
    cashInvalidator: useInvalidateMan(cargoCaseDocId),
    axios,
  });
}

/**
 * Creates trigger function postAddIndossament
 * @param {import("axios").AxiosInstance} axios
 * @param {string | undefined} cargoCaseDocId
 */

export function usePostAddIndossament(axios, cargoCaseDocId) {
  const web3Handler = useWeb3Handler(axios);
  const cashInvalidation = useInvalidateBol(cargoCaseDocId);
  /**
   * Trigger function : triggers request INDOSSAMENT_POST_ADD
   * @param {{
   *  indossamentData : {
   *      bol_id : string,
   *      indossant_id : string,
   *      indossat_id : string
   * },
   *  sign : string,
   * }} params
   * @param {{
   *  onSuccess : (() => void) | undefined
   *  onError : ((error : string) => void) | undefined
   *  onFinally : (() => void) | undefined
   * } | undefined} callbacks
   * @returns {Promise<void>}
   */
  return function postAddIndossament({ indossamentData, sign }, callbacks) {
    return axios
      .post(INDOSSAMENT_POST_ADD, {
        bol_id: indossamentData.bol_id,
        indossant_id: indossamentData.indossant_id,
        indossat_id: indossamentData.indossat_id,
        sign: sign,
      })
      .then(web3Handler())
      .then(cashInvalidation(indossamentData.bol_id))
      .then(callbacks?.onSuccess ?? (() => {}))
      .catch(callbacks?.onError ?? (() => {}))
      .then(callbacks?.onFinally ?? (() => {}));
  };
}

// Type definitions ---------------------------------------------------------------------------------

/**
 * Callbacks used for sign functions
 * @typedef {({
 *  onSuccess : (() => void) | undefined
 *  onError : ((error : *) => void) | undefined
 *  onFinally : (() => void) | undefined
 * } | undefined)} AsyncCallbacks
 */

/**
 * Use sign hook return type
 * @typedef {(makeRequestOptions : {id : string, body : *}, callbacks : AsyncCallbacks, description : string | undefined) => Promise<void>} SignHookReturn
 */

/**
 * Use sign hook
 * @typedef {(axios : import("axios").AxiosInstance, cargoCaseDocId : string) => () => SignHookReturn} SignHook
 */
